import React, { useState, useEffect, useCallback } from 'react';
import { getCacheStats, syncPendingOps, getPendingOps } from '../../services/offlineDB';
import api from '../../services/api';

const OfflineStatusBar = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [stats, setStats] = useState({ animalesCached: 0, pendingOps: 0, lastCacheTime: null });
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const updateStats = useCallback(async () => {
    try {
      const s = await getCacheStats();
      setStats(s);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    const goOnline = () => { setOnline(true); handleSync(); };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    updateStats();
    const interval = setInterval(updateStats, 10000);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); clearInterval(interval); };
  }, [updateStats]);

  const handleSync = async () => {
    if (syncing || !navigator.onLine) return;
    setSyncing(true);
    try {
      const result = await syncPendingOps(api);
      setLastResult(result);
      await updateStats();
      if (result.synced > 0) {
        // Notify the app that data was synced - refresh pages
        window.dispatchEvent(new CustomEvent('tkorvex-sync-complete', { detail: result }));
        setTimeout(() => setLastResult(null), 5000);
      }
    } catch { /* */ }
    finally { setSyncing(false); }
  };

  // Auto-dismiss the bar after successful sync with no pending
  useEffect(() => {
    if (lastResult && lastResult.synced > 0 && stats.pendingOps === 0) {
      const timer = setTimeout(() => setLastResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [lastResult, stats.pendingOps]);

  // Only show when relevant
  if (online && stats.pendingOps === 0 && !lastResult) return null;

  const barColor = !online
    ? 'bg-amber-600 text-white'
    : stats.pendingOps > 0
      ? 'bg-blue-600 text-white'
      : 'bg-green-600 text-white';

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 ${barColor} transition-all`} data-testid="offline-status-bar">
      <div className="px-4 py-2 flex items-center justify-between text-sm">
        <div className="flex items-center space-x-3">
          <span className={`w-2.5 h-2.5 rounded-full ${online ? 'bg-green-300' : 'bg-red-300'} animate-pulse`} />
          <span className="font-medium">
            {online ? 'En linea' : 'Modo Campo — Sin conexion'}
          </span>
          {stats.animalesCached > 0 && (
            <span className="px-2 py-0.5 bg-white/20 rounded text-xs">{stats.animalesCached} animales en cache</span>
          )}
          {stats.pendingOps > 0 && (
            <span className="px-2 py-0.5 bg-yellow-400/30 rounded text-xs font-bold">{stats.pendingOps} pendiente{stats.pendingOps !== 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {lastResult && <span className="text-xs opacity-80">{lastResult.synced} sincronizado{lastResult.synced !== 1 ? 's' : ''}</span>}
          {online && stats.pendingOps > 0 && (
            <button onClick={handleSync} disabled={syncing}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-medium"
              data-testid="sync-btn">{syncing ? 'Sincronizando...' : 'Sincronizar'}</button>
          )}
          <button onClick={() => setExpanded(!expanded)} className="px-2 py-1 bg-white/10 rounded text-xs">
            {expanded ? 'Cerrar' : 'Info'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-white/20 text-xs space-y-1">
          <p>Animales en cache: <strong>{stats.animalesCached}</strong></p>
          <p>Pesajes en cache: <strong>{stats.pesajesCached || 0}</strong></p>
          <p>Servicios en cache: <strong>{stats.serviciosCached || 0}</strong></p>
          <p>Pariciones en cache: <strong>{stats.paricionesCached || 0}</strong></p>
          <p>Operaciones pendientes: <strong>{stats.pendingOps}</strong></p>
          <p>Ultimo cache: <strong>{stats.lastCacheTime ? new Date(stats.lastCacheTime).toLocaleString() : 'Nunca'}</strong></p>
          {!online && (
            <p className="mt-1 bg-white/10 p-2 rounded">
              Podes registrar pesajes, servicios, pariciones y controles sin internet.
              Los datos se guardan localmente y se sincronizan al volver a conectarte.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineStatusBar;
